import { computed, reactive, shallowRef } from 'vue'

/**
 * The sub-areas of the page on screen (package 22C, Arnold: N1), shared between the page and the
 * second navigation column. A page registers its areas (PageAreas.vue); the column
 * (SectionNavColumn.vue) shows them as indented sub-rows under the row of that page and reports
 * that it does, so the page renders its own tab row only where the column does not show them.
 *
 * One registration at a time. Each one carries an owner token: a page that unmounts clears only
 * its own registration, never the one the next page made in the meantime.
 *
 * @typedef {{ key: string, label: string, count?: string|number|null }} PageArea
 * @typedef {{
 *   owner: symbol,
 *   pageKey: string|null,
 *   label: string,
 *   items: Array<PageArea>,
 *   active: string|null,
 *   select: (key: string) => void,
 * }} PageAreasRegistration
 */

/** @type {import('vue').ShallowRef<PageAreasRegistration|null>} */
const registration = shallowRef(null)

/** Columns that currently show the registered areas. */
const hosts = reactive(new Set())

/**
 * @param {{ pageKey: string|null, label: string, items: Array<PageArea>, active: string|null, select: (key: string) => void }} areas
 * @returns {symbol} the owner token for updatePageAreas and clearPageAreas
 */
export const registerPageAreas = (areas) => {
  const owner = Symbol('page-areas')
  registration.value = { ...areas, owner }

  return owner
}

/**
 * Updates the owner's registration (new counts, another active area); ignored once another page
 * has registered.
 *
 * @param {symbol} owner
 * @param {Partial<Omit<PageAreasRegistration, 'owner'>>} changes
 */
export const updatePageAreas = (owner, changes) => {
  if (registration.value?.owner !== owner) {
    return
  }

  registration.value = { ...registration.value, ...changes, owner }
}

/**
 * @param {symbol} owner
 */
export const clearPageAreas = (owner) => {
  if (registration.value?.owner === owner) {
    registration.value = null
  }
}

/** The registered areas, or null. */
export const pageAreas = computed(() => registration.value)

/**
 * A column reports whether it shows the registered areas.
 *
 * @param {symbol} host
 * @param {boolean} shown
 */
export const setPageAreasHost = (host, shown) => {
  if (shown) {
    hosts.add(host)
  } else {
    hosts.delete(host)
  }
}

/** Whether a navigation column shows the registered areas (from lg up). */
export const pageAreasInColumn = computed(
  () => registration.value !== null && hosts.size > 0,
)
